import { useState } from "react";
import { useFeature } from "@/app/subscription";
import {
  Alert,
  Button,
  Card,
  InputText,
  Select,
  TextArea,
  type SelectOption,
} from "@/deps/ui-library";
import { useApprovalRules, useSaveApprovalRules, useUsers } from "@/api/queries";
import type { ApprovalMode, ApprovalRule, Id, RoleName, UserRow } from "@/api/types";
import { useCan } from "@/app/session";
import { useToast } from "@/app/toasts";
import { Dialog } from "@/components/Dialog";
import { PageHeader } from "@/components/PageHeader";
import { formatAmount } from "@/lib/money";

const DOCUMENTS: Record<string, string> = {
  sales_invoice: "Invoice",
  credit_note: "Credit note",
  supplier_bill: "Bill",
  supplier_debit_note: "Debit note",
  fund_transfer: "Fund transfer",
  supplier_payment: "Payment",
  purchase_order: "Purchase order",
  manual_journal: "Journal",
  year_close: "Year-end close",
};

const MODES: SelectOption[] = [
  { value: "off", label: "Off" },
  { value: "always", label: "Always" },
  { value: "above", label: "Above amount" },
];

const LEVELS: SelectOption[] = [
  { value: "1", label: "One approval" },
  { value: "2", label: "Two approvals" },
];

/** Without Pro's two-step approvals, a rule that hasn't two levels can't be given them (suite ADR-0096). */
const ONE_LEVEL: SelectOption[] = [
  { value: "1", label: "One approval" },
  { value: "2", label: "Two approvals · Pro", disabled: true },
];

const MODE_LABELS: Record<ApprovalMode, string> = {
  off: "Off",
  always: "Always",
  above: "Above threshold",
};

const AMOUNT = /^\d+(\.\d{1,2})?$/;

/** Who a level can name: the roles that can take that step. */
const CAN_VERIFY: RoleName[] = ["Administrator", "Approver", "Verifier"];
const CAN_APPROVE: RoleName[] = ["Administrator", "Approver"];

const picked = (details: { value: unknown }) => {
  const value = Array.isArray(details.value) ? details.value[0] : details.value;
  return String((value as { value?: unknown } | undefined)?.value ?? "");
};

/** Why these can't be saved, or null. The API refuses the same things. */
function problemWith(rules: ApprovalRule[]): string | null {
  for (const rule of rules) {
    const name = DOCUMENTS[rule.documentType] ?? rule.documentType;
    if (
      rule.mode === "above" &&
      !(AMOUNT.test(rule.threshold ?? "") && Number(rule.threshold) > 0)
    ) {
      return `${name}: enter the amount above which it needs approval.`;
    }
    if (
      rule.mode !== "off" &&
      rule.levels === 2 &&
      rule.level1Approvers.length === 1 &&
      rule.level1Approvers[0] === rule.level2Approvers[0] &&
      rule.level2Approvers.length === 1
    ) {
      return `${name}: two approvals need two people.`;
    }
  }
  return null;
}

/**
 * Which documents need sign-off, and from whom. Only documents something enforces are listed, so what
 * this page says is what happens (ADR-0053).
 */
export function ApprovalRulesPage() {
  const can = useCan();
  const { data: saved, isPending } = useApprovalRules();
  const canEdit = can("settings.approvals.manage");
  const { data: users } = useUsers(canEdit);
  const save = useSaveApprovalRules();
  const toast = useToast();
  const [draft, setDraft] = useState<ApprovalRule[] | null>(null);
  const [asking, setAsking] = useState(false);
  const [reason, setReason] = useState("");
  const [problem, setProblem] = useState<string | null>(null);

  const rows = draft ?? saved ?? [];
  const editing = draft !== null;

  const patch = (documentType: string, change: Partial<ApprovalRule>) =>
    setDraft((current) =>
      (current ?? []).map((rule) =>
        rule.documentType === documentType ? { ...rule, ...change } : rule,
      ),
    );

  const finish = () => {
    const found = problemWith(rows);
    setProblem(found);
    if (!found) setAsking(true);
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Approval rules"
        subtitle="A document keeps the rule it started under."
        actions={
          !canEdit ? undefined : editing ? (
            <span className="flex gap-2">
              <Button
                colorScheme="default"
                variant="outline"
                size="sm"
                onClick={() => {
                  setDraft(null);
                  setProblem(null);
                }}
              >
                Cancel
              </Button>
              <Button colorScheme="primary" variant="solid" size="sm" onClick={finish}>
                Save
              </Button>
            </span>
          ) : (
            <Button
              colorScheme="default"
              variant="outline"
              size="sm"
              disabled={!saved}
              onClick={() => setDraft(saved ?? [])}
            >
              Edit
            </Button>
          )
        }
      />

      {problem ? (
        <Alert config={{ variant: "subtle", colorScheme: "danger" }} description={problem} />
      ) : null}

      <Card size="none" className="overflow-x-auto">
        {isPending ? (
          <p className="text-fg-muted p-4 text-sm">Loading</p>
        ) : (
          <table className="w-full min-w-[820px] border-collapse text-sm">
            <thead>
              <tr className="bg-muted text-foreground text-left text-xs font-medium">
                <th className="px-3 py-2.5 font-medium">Document</th>
                <th className="w-40 px-3 py-2.5 font-medium">Requires approval</th>
                <th className="w-36 px-3 py-2.5 font-medium">Threshold</th>
                <th className="w-40 px-3 py-2.5 font-medium">Levels</th>
                <th className="px-3 py-2.5 font-medium">Approvers</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((rule) => (
                <Row
                  key={rule.documentType}
                  rule={rule}
                  users={users ?? []}
                  editing={editing}
                  onChange={(change) => patch(rule.documentType, change)}
                />
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <p className="text-fg-muted text-xs">
        With no approvers chosen, anyone whose role allows it can act. Whoever raised a document can
        never approve it.
      </p>

      <Dialog
        open={asking}
        onClose={() => setAsking(false)}
        title="Why are you changing these?"
        description="This goes on the audit trail with the change, and can't be removed."
        footer={
          <Button
            colorScheme="primary"
            variant="solid"
            size="sm"
            disabled={reason.trim() === "" || save.isPending}
            onClick={() =>
              save.mutate(
                { rules: rows, reason },
                {
                  onSuccess: () => {
                    setAsking(false);
                    setDraft(null);
                    setReason("");
                    toast({ title: "Approval rules saved.", scheme: "success" });
                  },
                },
              )
            }
          >
            {save.isPending ? "Saving" : "Save"}
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          {save.isError ? (
            <Alert
              config={{ variant: "subtle", colorScheme: "danger" }}
              description={(save.error as Error).message}
            />
          ) : null}
          <TextArea
            label="Reason"
            required
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
      </Dialog>
    </div>
  );
}

function Row({
  rule,
  users,
  editing,
  onChange,
}: {
  rule: ApprovalRule;
  users: UserRow[];
  editing: boolean;
  onChange: (change: Partial<ApprovalRule>) => void;
}) {
  const name = DOCUMENTS[rule.documentType] ?? rule.documentType;
  const twoStep = useFeature("TWO_STEP_APPROVALS");
  const off = rule.mode === "off";
  const two = !off && rule.levels === 2;

  return (
    <tr className="border-border border-t align-top">
      <td className="px-3 py-2.5 font-medium">{name}</td>
      <td className="px-3 py-2.5">
        {editing ? (
          <Select
            aria-label={`${name} requires approval`}
            options={MODES}
            value={rule.mode}
            showClearButton={false}
            onChange={(details) => onChange({ mode: picked(details) as ApprovalMode })}
          />
        ) : (
          <span className={off ? "text-fg-muted" : "text-fg"}>{MODE_LABELS[rule.mode]}</span>
        )}
      </td>
      <td className="px-3 py-2.5">
        {rule.mode !== "above" ? (
          <span className="text-fg-hint">—</span>
        ) : editing ? (
          <InputText
            aria-label={`${name} threshold`}
            placeholder="0.00"
            value={rule.threshold ?? ""}
            onChange={(event) => onChange({ threshold: event.target.value })}
          />
        ) : (
          <span className="num">{formatAmount(rule.threshold ?? "0")}</span>
        )}
      </td>
      <td className="px-3 py-2.5">
        {off ? (
          <span className="text-fg-hint">—</span>
        ) : editing ? (
          <Select
            aria-label={`${name} levels`}
            options={twoStep || rule.levels === 2 ? LEVELS : ONE_LEVEL}
            value={String(rule.levels)}
            showClearButton={false}
            onChange={(details) => {
              const levels = picked(details) === "2" ? 2 : 1;
              onChange({ levels, level2Approvers: levels === 1 ? [] : rule.level2Approvers });
            }}
          />
        ) : (
          <span>{rule.levels === 2 ? "Two approvals" : "One approval"}</span>
        )}
      </td>
      <td className="px-3 py-2.5">
        {off ? (
          <span className="text-fg-hint">—</span>
        ) : (
          <div className="flex flex-col gap-2">
            <People
              label={two ? "Level 1" : undefined}
              ariaLabel={`${name} ${two ? "first level" : ""} approvers`}
              chosen={rule.level1Approvers}
              users={users.filter((u) => (two ? CAN_VERIFY : CAN_APPROVE).includes(u.role))}
              allUsers={users}
              editing={editing}
              onChange={(level1Approvers) => onChange({ level1Approvers })}
            />
            {two ? (
              <People
                label="Level 2"
                ariaLabel={`${name} second level approvers`}
                chosen={rule.level2Approvers}
                users={users.filter((u) => CAN_APPROVE.includes(u.role))}
                allUsers={users}
                editing={editing}
                onChange={(level2Approvers) => onChange({ level2Approvers })}
              />
            ) : null}
          </div>
        )}
      </td>
    </tr>
  );
}

function People({
  label,
  ariaLabel,
  chosen,
  users,
  allUsers,
  editing,
  onChange,
}: {
  label: string | undefined;
  ariaLabel: string;
  chosen: Id[];
  users: UserRow[];
  allUsers: UserRow[];
  editing: boolean;
  onChange: (people: Id[]) => void;
}) {
  const nameOf = (id: Id) => allUsers.find((u) => u.id === id)?.fullName ?? "Someone";

  return (
    <div>
      {label ? <span className="text-fg-hint mb-1 block text-xs">{label}</span> : null}
      {editing ? (
        <div role="group" aria-label={ariaLabel} className="flex flex-wrap gap-1">
          {users.map((user) => {
            const on = chosen.includes(user.id);
            return (
              <Button
                key={user.id}
                colorScheme={on ? "primary" : "default"}
                variant={on ? "subtle" : "outline"}
                size="xs"
                aria-pressed={on}
                onClick={() =>
                  onChange(on ? chosen.filter((id) => id !== user.id) : [...chosen, user.id])
                }
              >
                {user.fullName}
              </Button>
            );
          })}
          {users.length === 0 ? (
            <span className="text-fg-muted text-xs">Nobody here has a role that can do this.</span>
          ) : null}
        </div>
      ) : chosen.length === 0 ? (
        <span className="text-fg-muted text-xs">Anyone whose role allows it</span>
      ) : (
        <span className="text-xs">{chosen.map(nameOf).join(", ")}</span>
      )}
    </div>
  );
}
